import { customerRouteHref } from '@/lib/navigation/canonicalRoutes';
import {
  WORKSPACE_RETURN_SURFACES,
  type WorkspaceReturnSurface,
} from '@/lib/navigation/returnTarget';
import type { User } from '@/types';
import type { MvpAccountRole } from '@/types';
import {
  canViewSecurity,
  getAccountCapabilities,
  getSecurityCapabilitiesForUser,
  type Capability,
} from './capabilities';
import {
  listAccessibleWorkspaces,
  type AccessibleWorkspace,
} from './accessibleWorkspaces';
import { lookupRuntimeWorkspace } from './runtimeWorkspaceLookup';
import { resolveIdentityRoles } from './identityRoles';
import { getActiveScopedRelationships } from './scopedRelationshipResolver';
import { canEnterWorkspaceSurface } from './workspaceSurfaceEntry';
import {
  COMPANY_PORTAL_LABEL,
  companyPortalHref,
  resolveCompanyPortalEntry,
} from './companyPortal';

export interface ResolvedRouteAuthority {
  readonly user: User | null | undefined;
  readonly role: MvpAccountRole | null;
  readonly accountRole: ReturnType<typeof resolveIdentityRoles>['accountRole'];
  readonly platformRole: ReturnType<typeof resolveIdentityRoles>['platformRole'];
  readonly capabilities: readonly Capability[];
  readonly accessibleWorkspaces: readonly AccessibleWorkspace[];
  readonly hasAccessibleWorkspace: boolean;
  readonly surfaceEntries: Readonly<Record<WorkspaceReturnSurface, boolean>>;
  readonly lifecycleVersion: string;
}

export interface SafeAuthorizedDestination {
  readonly to: string;
  readonly label: string;
}

function canEnterResolvedSurface(
  surface: WorkspaceReturnSurface,
  user: User | null | undefined,
  capabilities: readonly Capability[],
  hasAccessibleWorkspace: boolean,
  platformRole: ReturnType<typeof resolveIdentityRoles>['platformRole'],
): boolean {
  if (platformRole) {
    return surface === 'settings';
  }

  switch (surface) {
    case 'dashboard':
      return hasAccessibleWorkspace;
    case 'settings':
      return hasAccessibleWorkspace;
    case 'security':
      return hasAccessibleWorkspace && canViewSecurity([...capabilities]);
    case 'projects':
    case 'chat':
    case 'milestones':
    case 'tasks':
    case 'assets':
    case 'documents':
    case 'companies':
    case 'reports':
    case 'investments':
    case 'decisions':
    case 'users':
      return hasAccessibleWorkspace && canEnterWorkspaceSurface(surface, user, capabilities);
    default:
      return false;
  }
}

export function resolveRouteAuthority(
  user: User | null | undefined,
): ResolvedRouteAuthority {
  const { accountRole, platformRole } = resolveIdentityRoles(user);
  const role = platformRole ?? accountRole;
  const accountCapabilities = getAccountCapabilities(user);
  const securityCapabilities = getSecurityCapabilitiesForUser(user)
    .filter((capability) => capability.startsWith('security.'));
  const capabilities = [...new Set([...accountCapabilities, ...securityCapabilities])];
  const accessibleWorkspaces = listAccessibleWorkspaces(user, (workspaceId) =>
    lookupRuntimeWorkspace(workspaceId, user),
  );
  const hasAccessibleWorkspace = accessibleWorkspaces.length > 0;

  const surfaceEntries = Object.fromEntries(
    WORKSPACE_RETURN_SURFACES.map((surface) => [
      surface,
      canEnterResolvedSurface(surface, user, capabilities, hasAccessibleWorkspace, platformRole),
    ]),
  ) as Record<WorkspaceReturnSurface, boolean>;

  return {
    user,
    role,
    accountRole,
    platformRole,
    capabilities,
    accessibleWorkspaces,
    hasAccessibleWorkspace,
    surfaceEntries,
    lifecycleVersion: [
      user?.id ?? 'anon',
      role ?? 'none',
      accessibleWorkspaces.map((workspace) => workspace.id).join(','),
      (user?.scopedRelationships ?? []).map((relationship) =>
        `${relationship.type}:${relationship.resourceType}:${relationship.resourceId}:${relationship.status}`,
      ).join('|'),
    ].join('::'),
  };
}

/**
 * Resolves a landing route from active authority facts. It is intentionally
 * shared by recovery UI and Demo persona switching so neither path can grant
 * access that the route/surface model would deny.
 */
export function resolveSafeLandingDestination(
  authority: ResolvedRouteAuthority,
): SafeAuthorizedDestination {
  if (!authority.user) {
    return { to: '/login', label: 'Sign In' };
  }
  if (authority.user.status === 'suspended') {
    return { to: '/suspended', label: 'Account Suspended' };
  }
  if (authority.platformRole) {
    return { to: '/platform', label: 'Go to Control Plane' };
  }
  const relationshipTypes = new Set(
    getActiveScopedRelationships(authority.user).map((relationship) => relationship.type),
  );
  if (relationshipTypes.has('investor_representative') || relationshipTypes.has('investment_manager')) {
    return { to: '/investor', label: 'Go to Investor Portal' };
  }
  if (authority.accessibleWorkspaces.length > 1) {
    return { to: '/workspaces/select', label: 'Choose Workspace' };
  }
  if (authority.accessibleWorkspaces.length === 1) {
    const workspaceId = authority.accessibleWorkspaces[0].id;
    if (relationshipTypes.has('reporting_recipient') && authority.surfaceEntries.reports) {
      return {
        to: customerRouteHref(workspaceId, 'reports'),
        label: 'Go to Assigned Reports',
      };
    }
    if (
      (relationshipTypes.has('decision_participant') || relationshipTypes.has('validator'))
      && authority.surfaceEntries.decisions
    ) {
      return {
        to: customerRouteHref(workspaceId, 'decisions'),
        label: 'Go to Assigned Decisions',
      };
    }
    if (
      (relationshipTypes.has('project_manager') || relationshipTypes.has('project_contributor') || relationshipTypes.has('observer'))
      && authority.surfaceEntries.projects
    ) {
      return {
        to: customerRouteHref(workspaceId, 'projects'),
        label: 'Go to Assigned Projects',
      };
    }
    if (relationshipTypes.has('company_representative') && authority.surfaceEntries.companies) {
      // Prefer canonical Company Portal when a single represented company is known.
      const portalEntry = resolveCompanyPortalEntry(authority.user);
      if (portalEntry.kind === 'one') {
        return {
          to: companyPortalHref(portalEntry.companyId, 'dashboard'),
          label: `Go to ${COMPANY_PORTAL_LABEL}`,
        };
      }
      return {
        to: customerRouteHref(workspaceId, 'companies'),
        label: 'Go to Represented Company',
      };
    }
    if (relationshipTypes.has('partner_representative')) {
      return {
        to: customerRouteHref(workspaceId, 'projects'),
        label: 'Go to Partner Assigned Work',
      };
    }
    if (relationshipTypes.has('vendor_representative')) {
      return {
        to: customerRouteHref(workspaceId, 'projects'),
        label: 'Go to Vendor Assigned Work',
      };
    }
    return {
      to: customerRouteHref(workspaceId, 'dashboard'),
      label: 'Go to Available Workspace',
    };
  }
  return { to: '/unauthorized', label: 'No Available Workspace' };
}

/** @deprecated Use resolveSafeLandingDestination. */
export const resolveSafeAuthorizedDestination = resolveSafeLandingDestination;
